import React, { useState, useRef, useEffect } from 'react';
import { 
  Paperclip, Smile, Mic, MicOff, X, Play, Pause, 
  Image as ImageIcon, File, Download, Volume2, StopCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/config/api';

// Common emoji reactions
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🎉', '🔥', '👏'];

// Attachment Preview Component
export const AttachmentPreview = ({ attachment, onRemove, isUpload = false }) => {
  if (!attachment) return null;

  const isImage = attachment.type === 'image';
  const isAudio = attachment.type === 'audio';

  if (isImage) {
    return (
      <div className="relative inline-block">
        <img 
          src={attachment.url} 
          alt={attachment.filename}
          className="max-h-48 rounded-lg object-cover"
        />
        {isUpload && onRemove && (
          <button
            onClick={onRemove}
            className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 hover:bg-red-600"
          >
            <X size={14} className="text-white" />
          </button>
        )}
      </div>
    );
  }

  if (isAudio) {
    return (
      <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2">
        <Volume2 size={18} className="text-white/70" />
        <audio controls className="h-8 max-w-[200px]">
          <source src={attachment.url} type={attachment.mime_type} />
        </audio>
        {attachment.duration && (
          <span className="text-xs text-white/50">{Math.round(attachment.duration)}s</span>
        )}
        {isUpload && onRemove && (
          <button onClick={onRemove} className="ml-1 text-red-400 hover:text-red-300">
            <X size={14} />
          </button>
        )}
      </div>
    );
  }

  // File attachment
  return (
    <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2">
      <File size={18} className="text-white/70" />
      <span className="text-sm text-white truncate max-w-[150px]">{attachment.filename}</span>
      <span className="text-xs text-white/50">({Math.round(attachment.size / 1024)}KB)</span>
      {!isUpload && (
        <a 
          href={attachment.url} 
          download={attachment.filename}
          className="ml-1 text-blue-400 hover:text-blue-300"
        >
          <Download size={14} />
        </a>
      )}
      {isUpload && onRemove && (
        <button onClick={onRemove} className="ml-1 text-red-400 hover:text-red-300">
          <X size={14} />
        </button>
      )}
    </div>
  );
};

// Reactions Display Component
export const MessageReactions = ({ reactions = [], onAddReaction, onRemoveReaction, currentUserId }) => {
  const [showPicker, setShowPicker] = useState(false);

  // Group reactions by emoji
  const groupedReactions = reactions.reduce((acc, r) => {
    if (!acc[r.emoji]) {
      acc[r.emoji] = { emoji: r.emoji, users: [], count: 0 };
    }
    acc[r.emoji].users.push(r.user_name);
    acc[r.emoji].count++;
    acc[r.emoji].hasCurrentUser = acc[r.emoji].hasCurrentUser || r.user_id === currentUserId;
    return acc;
  }, {});

  return (
    <div className="flex flex-wrap items-center gap-1 mt-1">
      {Object.values(groupedReactions).map((reaction) => (
        <button
          key={reaction.emoji}
          onClick={() => {
            if (reaction.hasCurrentUser) {
              onRemoveReaction?.(reaction.emoji);
            } else {
              onAddReaction?.(reaction.emoji);
            }
          }}
          className={cn(
            "flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-colors",
            reaction.hasCurrentUser
              ? "bg-blue-500/30 text-blue-200 hover:bg-blue-500/40"
              : "bg-white/10 text-white/70 hover:bg-white/20"
          )}
          title={reaction.users.join(', ')}
        >
          <span>{reaction.emoji}</span>
          <span>{reaction.count}</span>
        </button>
      ))}
      
      {/* Add reaction button */}
      <div className="relative">
        <button
          onClick={() => setShowPicker(!showPicker)}
          className="p-1 rounded-full hover:bg-white/10 text-white/40 hover:text-white/70 transition-colors"
        >
          <Smile size={14} />
        </button>
        
        {showPicker && (
          <div className="absolute bottom-full mb-1 left-0 bg-[#1E3A5F] rounded-lg p-2 shadow-xl border border-white/20 z-10">
            <div className="flex gap-1">
              {QUICK_REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => {
                    onAddReaction?.(emoji);
                    setShowPicker(false);
                  }}
                  className="text-lg hover:scale-125 transition-transform p-1"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Voice Message Recorder Component
export const VoiceRecorder = ({ onRecorded, onCancel }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    setRecordingTime(0);
    onCancel?.();
  };

  const sendVoiceMessage = async () => {
    if (!audioBlob) return;
    
    const formData = new FormData();
    formData.append('audio', audioBlob, 'voice_message.webm');
    formData.append('duration', recordingTime.toString());
    
    onRecorded?.(audioBlob, recordingTime);
    setAudioBlob(null);
    setRecordingTime(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  return (
    <div className="flex items-center gap-3 bg-white/10 rounded-lg px-4 py-2">
      {!isRecording && !audioBlob && (
        <button
          onClick={startRecording}
          className="flex items-center gap-2 text-white hover:text-red-400 transition-colors"
        >
          <Mic size={20} />
          <span className="text-sm">Tap to record</span>
        </button>
      )}

      {isRecording && (
        <>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            <span className="text-white text-sm font-mono">{formatTime(recordingTime)}</span>
          </div>
          <button
            onClick={stopRecording}
            className="p-2 bg-red-500 rounded-full hover:bg-red-600 transition-colors"
          >
            <StopCircle size={18} className="text-white" />
          </button>
        </>
      )}

      {audioBlob && !isRecording && (
        <>
          <audio controls className="h-8 max-w-[200px]">
            <source src={URL.createObjectURL(audioBlob)} type="audio/webm" />
          </audio>
          <span className="text-xs text-white/50">{formatTime(recordingTime)}</span>
          <button
            onClick={sendVoiceMessage}
            className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Send
          </button>
        </>
      )}

      <button
        onClick={cancelRecording}
        className="text-white/50 hover:text-white transition-colors"
      >
        <X size={18} />
      </button>
    </div>
  );
};

// Enhanced Message Input Component
export const EnhancedMessageInput = ({
  value,
  onChange,
  onSend,
  placeholder,
  disabled,
  token,
  conversationId
}) => {
  const [attachments, setAttachments] = useState([]);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${BACKEND_URL}/api/dm/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (response.ok) {
          const attachment = await response.json();
          setAttachments(prev => [...prev, attachment]);
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setUploading(false);
    e.target.value = '';
  };

  const handleVoiceRecorded = async (blob, duration) => {
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('audio', blob, 'voice_message.webm');
      formData.append('duration', duration.toString());

      const response = await fetch(`${BACKEND_URL}/api/dm/upload-voice`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const attachment = await response.json();
        // Send voice message immediately
        onSend?.('', [attachment]);
      } else {
        toast.error('Failed to send voice message');
      }
    } catch (error) {
      console.error('Voice upload error:', error);
      toast.error('Failed to send voice message');
    }

    setUploading(false);
    setIsRecordingVoice(false);
  };

  const removeAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if ((!value.trim() && attachments.length === 0) || disabled) return;
    
    onSend?.(value, attachments);
    setAttachments([]);
  };

  const insertEmoji = (emoji) => {
    onChange({ target: { value: value + emoji } });
    setShowEmojiPicker(false);
  };

  if (isRecordingVoice) {
    return (
      <div className="p-4 border-t border-white/20 bg-[#0d1f35]">
        <VoiceRecorder
          onRecorded={handleVoiceRecorded}
          onCancel={() => setIsRecordingVoice(false)}
        />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-4 border-t border-white/20 bg-[#0d1f35]">
      {/* Attachment Previews */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {attachments.map((att) => (
            <AttachmentPreview
              key={att.id}
              attachment={att}
              onRemove={() => removeAttachment(att.id)}
              isUpload
            />
          ))}
        </div>
      )}

      <div className="flex items-center gap-3">
        {/* File Upload Button */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          className="hidden"
          multiple
          accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.txt"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => fileInputRef.current?.click()}
          className="text-white/60 hover:text-white hover:bg-white/10 h-10 w-10"
          disabled={uploading}
        >
          <Paperclip size={20} />
        </Button>

        {/* Voice Message Button */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => setIsRecordingVoice(true)}
          className="text-white/60 hover:text-white hover:bg-white/10 h-10 w-10"
        >
          <Mic size={20} />
        </Button>

        {/* Message Input */}
        <input
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="flex-1 bg-white/10 border border-white/30 text-white placeholder:text-white/50 h-11 text-base rounded-lg px-4 focus:outline-none focus:border-blue-500"
          data-testid="dm-message-input"
        />

        {/* Emoji Picker */}
        <div className="relative">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className="text-white/60 hover:text-white hover:bg-white/10 h-10 w-10"
          >
            <Smile size={20} />
          </Button>
          
          {showEmojiPicker && (
            <div className="absolute bottom-full mb-2 right-0 bg-[#1E3A5F] rounded-lg p-3 shadow-xl border border-white/20 z-10">
              <div className="grid grid-cols-8 gap-1">
                {['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊',
                  '😇', '🙂', '😉', '😌', '😍', '🥰', '😘', '😗',
                  '👍', '👎', '👏', '🙌', '🤝', '❤️', '🔥', '✨',
                  '🎉', '💯', '✅', '⭐', '💪', '🙏', '🤔', '😱'].map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => insertEmoji(emoji)}
                    className="text-xl hover:scale-125 transition-transform p-1 hover:bg-white/10 rounded"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Send Button */}
        <Button
          type="submit"
          disabled={(!value.trim() && attachments.length === 0) || disabled || uploading}
          className="bg-blue-600 hover:bg-blue-700 text-white h-10 px-4"
          data-testid="dm-send-btn"
        >
          {uploading ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <span>Send</span>
          )}
        </Button>
      </div>
    </form>
  );
};

export default EnhancedMessageInput;
